import React from 'react';
import Icon from '@mdi/react';
import {
    mdiCheckCircleOutline,
    mdiAlertCircleOutline,
    mdiInformationOutline,
    mdiClose,
} from '@mdi/js';
import { Box, SnackbarContent, IconButton, SxProps, Typography, Stack } from '@mui/material';
import { ceraProBold, ceraProRegular, vars } from '../../../Theme';
import { useSnackbar } from 'notistack';

/** Enum for Snackbar component */
export enum ToastSeverity {
    Success = 'success',
    Error = 'error',
    Info = 'info',
}

/**Icons for Snackbar component */
const variantIcon: Record<ToastSeverity, string> = {
    [ToastSeverity.Success]: mdiCheckCircleOutline,
    [ToastSeverity.Error]: mdiAlertCircleOutline,
    [ToastSeverity.Info]: mdiInformationOutline,
};

/** Colors for Snackbar component's background */
const colors: Record<ToastSeverity, string> = {
    [ToastSeverity.Success]: vars.mainGreen,
    [ToastSeverity.Error]: vars.errorRed,
    [ToastSeverity.Info]: vars.font,
};

/** Styles for SnackbarContent component */
const snackbarContentSx: SxProps = {
    '& .MuiSnackbarContent-message': {
        py: 0,
    },
    '&.success': {
        backgroundColor: vars.bgGreen,
    },
    '&.error': {
        backgroundColor: vars.errorBgRed,
    },
    '&.info': {
        backgroundColor: vars.lightGray,
    },
};

/** Styles for Box component */
const boxSx: SxProps = {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
};

/** Styles for message */
const messageSx: SxProps = {
    ...ceraProBold,
    fontSize: 16,
    color: vars.font,
    ml: '0.25rem',
};

/** Styles for additional info */
const additionalInfoSx: SxProps = {
    ...ceraProRegular,
    fontSize: 14,
    color: vars.font,
    ml: '2.5rem',
};

/**
 * Snackbar Component that allows to display notifications
 */
export default React.forwardRef<HTMLDivElement, ISnackbarProps>(
    ({ id, severity, message, additionalInfo }, ref) => {
        /** Allow to close the component */
        const { closeSnackbar } = useSnackbar();

        /**
         *  Close the component
         */
        const handleCloseSnackbar = () => {
            closeSnackbar(id);
        };

        return (
            <SnackbarContent
                ref={ref}
                sx={snackbarContentSx}
                className={severity}
                message={
                    <Stack>
                        <Box sx={boxSx}>
                            {variantIcon[severity] && (
                                <Icon
                                    color={colors[severity]}
                                    path={variantIcon[severity]}
                                    size={1.5}
                                />
                            )}
                            <Typography sx={messageSx}>{message}</Typography>
                        </Box>
                        {additionalInfo && (
                            <Typography sx={additionalInfoSx}>{additionalInfo}</Typography>
                        )}
                    </Stack>
                }
                action={
                    <IconButton size='small' onClick={handleCloseSnackbar}>
                        <Icon path={mdiClose} size={1} />
                    </IconButton>
                }
            />
        );
    },
);

/**
 * Interface that describes Snackbar's props.
 */
export interface ISnackbarProps {
    /** Id of the Snackbar component */
    id: number | string;

    /** Type of the Snackbar component
     * @example 'success', 'error', 'info'
     */
    severity?: ToastSeverity;

    /** Main message in the Snackbar component */
    message: string | React.ReactNode;

    /** Optianal info in the Snackbar component */
    additionalInfo?: string;
}
